import type { AdminTileLayer } from '~/common/types';

/** Toggle one layer's enabled flag, returning a new array (no mutation). */
export function toggleLayerEnabled(layers: AdminTileLayer[], key: string): AdminTileLayer[] {
  return layers.map((l) => (l.key === key ? { ...l, enabled: !l.enabled } : l));
}

/** Sorted keys of the disabled layers — the payload the save endpoint expects. */
export function disabledKeysOf(layers: AdminTileLayer[]): string[] {
  return layers
    .filter((l) => !l.enabled)
    .map((l) => l.key)
    .sort();
}

/** True when the current disabled set differs from the last-saved snapshot (order-independent). */
export function isDisabledDirty(current: string[], initial: string[]): boolean {
  const currentSet = new Set(current);
  const initialSet = new Set(initial);
  if (currentSet.size !== initialSet.size) return true;
  return [...currentSet].some((k) => !initialSet.has(k));
}

/**
 * Defense-in-depth for the Esri URL cell: only return the URL when it parses as
 * an http(s) URL. A malformed or `javascript:` value renders as plain text.
 */
export function safeHttpHref(url: string): string | null {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? url : null;
  } catch {
    return null;
  }
}

/** Case-insensitive filter over label + key + category + special-dataset slug + Esri URL. Empty/whitespace query returns all layers. */
export function filterLayers(layers: AdminTileLayer[], query: string): AdminTileLayer[] {
  const q = query.trim().toLowerCase();
  if (!q) return layers;
  return layers.filter(
    (l) =>
      l.label.toLowerCase().includes(q) ||
      l.key.toLowerCase().includes(q) ||
      l.category.toLowerCase().includes(q) ||
      (l.specialDatasetSlug?.toLowerCase().includes(q) ?? false) ||
      l.sourceUrl.toLowerCase().includes(q),
  );
}
